import { Download, Edit, Folder, Layers, LayersClear } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MenuItemLink } from '~/components/link';
import { TooltipMenuItem } from '~/components/menu-item';
import { useLink } from '~/hooks/link';
import { useLocation } from '~/hooks/location';
import type { ContentFolderView__MediaItem } from '../ContentFolderView.generated';

export interface ContentMenuMediaItemProps {
  /** Set by the menu when this is its first child; this component has no element to take it. */
  autoFocus?: boolean;
  canUpdate: boolean;
  item: ContentFolderView__MediaItem;
  onMove: () => void;
  onOverlay: () => void;
  onRemoveOverlay: () => void;
}

export const ContentMenuMediaItem = ({
  autoFocus,
  canUpdate,
  item,
  onMove,
  onOverlay,
  onRemoveOverlay,
}: ContentMenuMediaItemProps) => {
  const { t } = useTranslation();
  const link = useLink();
  const location = useLocation();
  return (
    <>
      <MenuItemLink
        autoFocus={autoFocus}
        download={item.originalFile.originalFilename}
        to={item.originalFile.downloadUri}
      >
        <ListItemIcon>
          <Download />
        </ListItemIcon>
        <ListItemText>{t('common.buttons.download')}</ListItemText>
      </MenuItemLink>

      <MenuItem disabled={!canUpdate} onClick={onMove}>
        <ListItemIcon>
          <Folder />
        </ListItemIcon>
        <ListItemText>{t('pages.content.menu.move_to')}</ListItemText>
      </MenuItem>

      {item.overlay ? (
        <TooltipMenuItem
          disabled={!item.overlay.canDestroy.value}
          onClick={onRemoveOverlay}
          tooltip={item.overlay.canDestroy.reasons?.fullMessages.join(', ') ?? ''}
        >
          <ListItemIcon>
            <LayersClear />
          </ListItemIcon>
          <ListItemText>{t('pages.content.menu.remove_overlay')}</ListItemText>
        </TooltipMenuItem>
      ) : (
        <MenuItem onClick={onOverlay}>
          <ListItemIcon>
            <Layers />
          </ListItemIcon>
          <ListItemText>{t('pages.content.menu.use_as_overlay')}</ListItemText>
        </MenuItem>
      )}

      <MenuItemLink
        to={link(`/content/${item.contentFolderId}/media/${item.id}/details`)}
        state={{ returnTo: location.fullPath }}
      >
        <ListItemIcon>
          <Edit />
        </ListItemIcon>
        <ListItemText>{t('common.actions.edit')}</ListItemText>
      </MenuItemLink>
    </>
  );
};
